import { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  HStack,
  IconButton,
  VStack,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../assets/logo-white.png";


const MotionBox = motion(Box);

export default function Navbar({ onOpenModal }) {
  const [scrolled, setScrolled] = useState(false);
  const [activePage, setActivePage] = useState("home");
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const navLinks = [
    { label: "Home", id: "home" },
    { label: "About", id: "about" },
    { label: "Contact", id: "contact" },
  ];

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setActivePage(id);
    onClose();
  };

  return (
    <>
      <MotionBox
        position="fixed"
        top="0"
        left="0"
        right="0"
        zIndex={1000}
        px={{ base: 6, md: 12, lg: 20 }}
        py={scrolled ? 4 : 6}
        bg={scrolled ? "rgba(13,13,13,0.96)" : "transparent"}
        backdropFilter={scrolled ? "blur(20px)" : "none"}
        borderBottom={
          scrolled ? "1px solid rgba(46,46,46,0.8)" : "none"
        }
        transition="all 0.4s ease"
      >
        <Flex align="center" justify="space-between" maxW="1400px" mx="auto">
          {/* Logo */}
          <Box cursor="pointer" onClick={() => scrollTo("home")}>
            <img
              src={logo}
              alt="Logo"
              style={{ height: 40, marginRight: 10 }}
            />
          </Box>

          {/* Desktop Nav */}
          <HStack spacing={10} display={{ base: "none", md: "flex" }}>
            {navLinks.map((l) => (
              <Box
                key={l.id}
                position="relative"
                cursor="pointer"
                onClick={() => scrollTo(l.id)}
                pb={1}
              >
                <Text
                  fontSize="xs"
                  letterSpacing="0.15em"
                  textTransform="uppercase"
                  fontWeight="500"
                  color={activePage === l.id ? "brand.coral" : "brand.cream"}
                  _hover={{ color: "brand.coral" }}
                  transition="color 0.3s"
                >
                  {l.label}
                </Text>
                {activePage === l.id && (
                  <MotionBox
                    layoutId="nav-indicator"
                    position="absolute"
                    bottom="-2px"
                    left="0"
                    right="0"
                    height="1px"
                    bg="brand.coral"
                  />
                )}
              </Box>
            ))}
            <Button variant="coral" size="sm" onClick={onOpenModal}>
              Get a Quote
            </Button>
          </HStack>

          {/* Mobile Hamburger */}
          <IconButton
            display={{ base: "flex", md: "none" }}
            variant="ghost"
            color="brand.cream"
            onClick={onOpen}
            _hover={{ bg: "transparent" }}
            aria-label="Open menu"
            icon={
              <Box>
                <Box w="22px" h="1.5px" bg="brand.cream" mb="5px" />
                <Box w="14px" h="1.5px" bg="brand.coral" mb="5px" />
                <Box w="22px" h="1.5px" bg="brand.cream" />
              </Box>
            }
          />
        </Flex>
      </MotionBox>

      {/* Mobile Drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
        <DrawerOverlay backdropFilter="blur(8px)" />
        <DrawerContent
          bg="brand.charcoal"
          borderLeft="1px solid rgba(46,46,46,0.8)"
        >
          <DrawerCloseButton color="brand.cream" mt={2} />
          <DrawerHeader pt={12} pb={6} borderBottom="1px solid rgba(46,46,46,0.6)">
            <Text
              fontFamily="'Playfair Display', serif"
              fontSize="xl"
              color="brand.coral"
              fontStyle="italic"
            >
              reliable touch.
            </Text>
            <Text
              fontSize="8px"
              letterSpacing="0.35em"
              color="brand.muted"
              textTransform="uppercase"
              mt="2px"
            >
              Services
            </Text>
          </DrawerHeader>
          <DrawerBody pt={10}>
            <VStack spacing={8} align="start">
              {navLinks.map((l) => (
                <Text
                  key={l.id}
                  fontSize="3xl"
                  fontFamily="'Playfair Display', serif"
                  cursor="pointer"
                  color={activePage === l.id ? "brand.coral" : "brand.cream"}
                  _hover={{ color: "brand.coral" }}
                  transition="color 0.3s"
                  onClick={() => scrollTo(l.id)}
                >
                  {l.label}
                </Text>
              ))}
              <Button
                variant="coral"
                w="full"
                mt={4}
                onClick={() => {
                  onOpenModal();
                  onClose();
                }}
              >
                Get a Quote
              </Button>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}
